import {
  ASSIGNEE_KIND_AI,
  ASSIGNEE_KIND_HUMAN,
  agentToAssigneeOption,
  assigneeOptionLabel,
  humanToAssigneeOption,
} from './ticketAssignees';

export const MENTION_KIND_HUMAN = 'human';
export const MENTION_KIND_AI = 'ai';
export const MENTION_KIND_GROUP = 'group';
export const MENTION_KIND_AI_USER = 'ai_user';

/**
 * @param {'human' | 'ai' | 'group' | 'ai_user'} kind
 * @param {string} id
 * @returns {string}
 */
export function toMentionEntityId(kind, id) {
  const safeId = typeof id === 'string' ? id.trim() : '';
  return `${kind}:${safeId}`;
}

/**
 * @param {string} entityId
 * @returns {{ kind: string, id: string } | null}
 */
export function parseMentionEntityId(entityId) {
  if (typeof entityId !== 'string' || !entityId.includes(':')) return null;
  const [kind, ...rest] = entityId.split(':');
  const id = rest.join(':').trim();
  if (!kind || !id) return null;
  return { kind, id };
}

/**
 * @param {object[]} humans
 * @param {object[]} aiUsers
 * @param {object[]} agents
 * @param {object[]} groups
 * @returns {object[]}
 */
export function buildTicketMentionItems(humans = [], aiUsers = [], agents = [], groups = []) {
  const humanItems = (humans || [])
    .filter((user) => user?.isAi !== true)
    .map((user) => {
      const option = humanToAssigneeOption(user);
      const label = assigneeOptionLabel(option);
      const uid = option.uid;
      return {
        id: toMentionEntityId(MENTION_KIND_HUMAN, uid),
        label,
        mentionKind: MENTION_KIND_HUMAN,
        uid,
        searchText: [label, option.email, option.displayName, uid].filter(Boolean).join(' ').toLowerCase(),
      };
    });

  const aiUserItems = (aiUsers || []).map((user) => {
    const uid = user.uid || user.id;
    const label = user.displayName || user.fullName || user.email || uid || 'AI User';
    return {
      id: toMentionEntityId(MENTION_KIND_AI_USER, uid),
      label,
      mentionKind: MENTION_KIND_AI_USER,
      uid,
      searchText: [label, user.email, uid].filter(Boolean).join(' ').toLowerCase(),
    };
  });

  const agentItems = (agents || [])
    .filter((agent) => !agent.status || agent.status === 'active')
    .map((agent) => {
      const option = agentToAssigneeOption(agent);
      const label = assigneeOptionLabel(option);
      const agentId = option.agentId;
      return {
        id: toMentionEntityId(MENTION_KIND_AI, agentId),
        label,
        mentionKind: MENTION_KIND_AI,
        agentId,
        roleTitle: option.roleTitle || '',
        searchText: [label, option.name, option.roleTitle, agentId].filter(Boolean).join(' ').toLowerCase(),
      };
    });

  const groupItems = (groups || []).map((group) => {
    const groupId = group.id;
    const label = group.name || group.title || 'Group';
    const memberCount = Array.isArray(group.members) ? group.members.length : 0;
    return {
      id: toMentionEntityId(MENTION_KIND_GROUP, groupId),
      label: `${label}${memberCount ? ` (${memberCount})` : ''}`,
      mentionKind: MENTION_KIND_GROUP,
      groupId,
      members: Array.isArray(group.members) ? group.members : [],
      searchText: [label, group.description, groupId].filter(Boolean).join(' ').toLowerCase(),
    };
  });

  return [...humanItems, ...aiUserItems, ...agentItems, ...groupItems].sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/**
 * @param {object} item
 * @returns {object}
 */
export function mentionRecordFromMentionItem(item) {
  if (!item) return null;
  if (item.mentionKind === MENTION_KIND_AI) {
    return {
      kind: MENTION_KIND_AI,
      entityId: item.id,
      agentId: item.agentId,
      displayName: item.label,
      roleTitle: item.roleTitle || '',
    };
  }
  if (item.mentionKind === MENTION_KIND_GROUP) {
    return {
      kind: MENTION_KIND_GROUP,
      entityId: item.id,
      groupId: item.groupId,
      displayName: item.label,
      members: item.members || [],
    };
  }
  if (item.mentionKind === MENTION_KIND_AI_USER) {
    return {
      kind: MENTION_KIND_AI_USER,
      entityId: item.id,
      uid: item.uid,
      displayName: item.label,
    };
  }
  return {
    kind: MENTION_KIND_HUMAN,
    entityId: item.id,
    uid: item.uid,
    displayName: item.label,
  };
}

/**
 * @param {string} html
 * @returns {object[]}
 */
export function extractMentionsFromHtml(html) {
  if (!html || typeof html !== 'string') return [];

  const rows = [];
  const seen = new Set();
  const patterns = [
    /data-type="mention"[^>]*data-id="([^"]+)"[^>]*data-label="([^"]*)"/gi,
    /data-id="([^"]+)"[^>]*data-type="mention"[^>]*data-label="([^"]*)"/gi,
    /data-mention-id="([^"]+)"[^>]*data-mention-label="([^"]*)"/gi,
    /data-type="mention"[^>]*data-id="([^"]+)"/gi,
    /data-id="([^"]+)"[^>]*data-type="mention"/gi,
  ];

  patterns.forEach((pattern) => {
    let match = pattern.exec(html);
    while (match) {
      const entityId = (match[1] || '').trim();
      const label = (match[2] || '').trim();
      if (entityId && !seen.has(entityId)) {
        seen.add(entityId);
        const parsed = parseMentionEntityId(entityId);
        if (parsed) {
          rows.push({
            entityId,
            label,
            kind: parsed.kind,
            id: parsed.id,
          });
        }
      }
      match = pattern.exec(html);
    }
  });

  return rows;
}

/**
 * @param {string} body
 * @param {string} [bodyFormat]
 * @param {object[]} [structuredMentions]
 * @returns {object[]}
 */
export function extractMentionsFromCommentPayload(body, bodyFormat, structuredMentions = []) {
  const fromStructured = Array.isArray(structuredMentions)
    ? structuredMentions
        .filter((row) => row && typeof row === 'object')
        .map((row) => ({
          entityId: row.entityId || (
            row.kind === MENTION_KIND_AI && row.agentId ?
              toMentionEntityId(MENTION_KIND_AI, row.agentId) :
              row.kind === MENTION_KIND_HUMAN && row.uid ?
                toMentionEntityId(MENTION_KIND_HUMAN, row.uid) :
                row.kind === MENTION_KIND_GROUP && row.groupId ?
                  toMentionEntityId(MENTION_KIND_GROUP, row.groupId) :
                  null
          ),
          label: row.displayName || '',
          kind: row.kind,
          id: row.agentId || row.uid || row.groupId || null,
          members: row.members || [],
        }))
        .filter((row) => row.entityId)
    : [];

  if (bodyFormat === 'html') {
    const fromHtml = extractMentionsFromHtml(body);
    const merged = [...fromStructured];
    const seen = new Set(merged.map((row) => row.entityId));
    fromHtml.forEach((row) => {
      if (!seen.has(row.entityId)) merged.push(row);
    });
    return merged;
  }

  return fromStructured;
}

/**
 * @param {object[]} mentions
 * @param {object} mention
 * @returns {object[]}
 */
export function appendUniqueMention(mentions, mention) {
  if (!mention) return mentions || [];
  const rows = Array.isArray(mentions) ? [...mentions] : [];
  const key = mention.entityId || mention.agentId || mention.uid || mention.groupId;
  if (!key) return rows;
  const exists = rows.some((row) => {
    const rowKey = row.entityId || row.agentId || row.uid || row.groupId;
    return rowKey === key;
  });
  if (!exists) rows.push(mention);
  return rows;
}

/**
 * @param {object[]} humans
 * @param {object[]} agents
 * @returns {object[]}
 */
export function buildTicketMentionOptions(humans = [], agents = []) {
  return buildTicketMentionItems(humans, [], agents, []);
}

/**
 * @param {string} text
 * @param {number} caret
 * @returns {{ query: string, start: number } | null}
 */
export function findActiveMentionQuery(text, caret) {
  if (typeof text !== 'string' || caret < 0) return null;
  const before = text.slice(0, caret);
  const match = /(^|[\s([{])@([^\s@]{0,40})$/.exec(before);
  if (!match) return null;
  const query = match[2] || '';
  const start = before.length - query.length - 1;
  return { query, start };
}

/**
 * @param {string} htmlOrText
 * @returns {string}
 */
export function stripHtmlForMentionScan(htmlOrText) {
  if (!htmlOrText || typeof htmlOrText !== 'string') return '';
  return htmlOrText
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {object[]|null|undefined} mentions
 * @returns {boolean}
 */
export function mentionsIncludeAi(mentions) {
  return (mentions || []).some(
    (m) =>
      m &&
      (m.kind === MENTION_KIND_AI || m.kind === MENTION_KIND_AI_USER),
  );
}

/**
 * True when posting/editing would wake or target an AI agent.
 * @param {object|null|undefined} ticket
 * @param {object[]|null|undefined} mentions
 * @returns {boolean}
 */
export function commentIsAiDirected(ticket, mentions) {
  if (mentionsIncludeAi(mentions)) return true;
  return Boolean(ticket?.isAssignedToAi && ticket?.assigneeAgentId);
}
